// Game-library sort module: type, options, and sort function.

import type { GameLibraryConfig } from '@airhead-games/shared/types';
import type { SortOption } from '../../../components/collectionToolbar';
import {
  applySortDirection,
  type SortDirection,
  toTimestamp,
} from '../../../utilities/sort';

export const GAME_LIBRARY_SORT_FIELDS = [
  'releaseDate',
  'alphabetical',
  'playerCount',
  'duration',
] as const;
export type GameLibrarySortField = (typeof GAME_LIBRARY_SORT_FIELDS)[number];

export const DEFAULT_GAME_LIBRARY_SORT_FIELD: GameLibrarySortField =
  'releaseDate';
export const DEFAULT_GAME_LIBRARY_SORT_DIRECTION: SortDirection = 'desc';

export const GAME_LIBRARY_SORT_OPTIONS: SortOption<GameLibrarySortField>[] = [
  {
    value: 'releaseDate',
    label: 'Release Date',
    directionLabels: { desc: 'Newest', asc: 'Oldest' },
  },
  {
    value: 'alphabetical',
    label: 'Alphabetical',
    directionLabels: { asc: 'A > Z', desc: 'Z > A' },
  },
  {
    value: 'playerCount',
    label: 'Player Count',
    directionLabels: { asc: 'Min > Max', desc: 'Max > Min' },
  },
  {
    value: 'duration',
    label: 'Duration',
    directionLabels: { asc: 'Shortest', desc: 'Longest' },
  },
];

export const sortGameLibraryGames = (
  games: GameLibraryConfig[],
  field: GameLibrarySortField,
  direction: SortDirection
): GameLibraryConfig[] =>
  [...games].sort((a, b) => {
    if (field === 'releaseDate') {
      const primary =
        toTimestamp(a.gameWiki.releasedAt) - toTimestamp(b.gameWiki.releasedAt);
      if (primary !== 0) {
        return applySortDirection(primary, direction);
      }
    }

    if (field === 'alphabetical') {
      const primary = a.gameCore.gameName.localeCompare(b.gameCore.gameName);
      if (primary !== 0) {
        return applySortDirection(primary, direction);
      }
    }

    if (field === 'playerCount') {
      if (direction === 'asc') {
        const minPrimary =
          a.gameCore.gamePlayersMin - b.gameCore.gamePlayersMin;
        if (minPrimary !== 0) {
          return minPrimary;
        }

        const maxTiebreak =
          a.gameCore.gamePlayersMax - b.gameCore.gamePlayersMax;
        if (maxTiebreak !== 0) {
          return maxTiebreak;
        }
      } else {
        const maxPrimary =
          b.gameCore.gamePlayersMax - a.gameCore.gamePlayersMax;
        if (maxPrimary !== 0) {
          return maxPrimary;
        }

        const minTiebreak =
          b.gameCore.gamePlayersMin - a.gameCore.gamePlayersMin;
        if (minTiebreak !== 0) {
          return minTiebreak;
        }
      }
    }

    if (field === 'duration') {
      const primary = a.gameWiki.averageDuration - b.gameWiki.averageDuration;
      if (primary !== 0) {
        return applySortDirection(primary, direction);
      }
    }

    return a.gameCore.gameName.localeCompare(b.gameCore.gameName);
  });
